import { useState, useEffect, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, TrendingDown, Download, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#163A2E', '#C25934', '#5B7B61', '#D99C6A', '#8CA496', '#4A6B8A'];
const CATEGORIES = ['Office Expenses', 'Salary & Staff', 'Technology', 'Marketing', 'Financial Costs', 'Miscellaneous'];
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'UPI', 'Credit Card', 'Debit Card', 'Cheque', 'Crypto', 'Other'];
const emptyForm = {
  title: '',
  description: '',
  amount: '',
  category: '',
  payment_method: 'Cash',
  vendor: '',
  reference_no: '',
  date: new Date().toISOString().split('T')[0],
  notes: '',
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ by_category: {}, by_payment_method: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [periodFilter, setPeriodFilter] = useState(new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [eRes, sRes] = await Promise.all([
        api.get('/expenses', { params: { date_from: `${periodFilter}-01`, date_to: `${periodFilter}-31` } }),
        api.get('/expenses/summary', { params: { period: periodFilter } })
      ]);
      setExpenses(eRes.data);
      setSummary(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [periodFilter]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || '',
      description: e.description || '',
      amount: String(e.amount || ''),
      category: e.category || '',
      payment_method: e.payment_method || 'Cash',
      vendor: e.vendor || '',
      reference_no: e.reference_no || '',
      date: e.date || new Date().toISOString().split('T')[0],
      notes: e.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.amount || !form.category || !form.date) {
      toast.error('Title, Amount, Category and Date are required'); return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editing) { await api.put(`/expenses/${editing.id}`, payload); toast.success('Expense updated'); }
      else { await api.post('/expenses', payload); toast.success('Expense added'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.delete(`/expenses/${id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const downloadPdf = async (scope) => {
    setDownloading(scope);
    try {
      const params = scope === 'monthly' ? { month: periodFilter } : {};
      const resp = await api.get('/expenses/report/pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = scope === 'monthly' ? `quantora-expenses-${periodFilter}.pdf` : 'quantora-expenses-all-time.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Expense report downloaded');
    } catch (err) {
      let msg = 'Failed to download';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) msg = JSON.parse(txt).detail || msg;
      } catch (_) {}
      toast.error(msg);
    } finally { setDownloading(null); }
  };

  const chartData = Object.entries(summary.by_category || {}).map(([k, v]) => ({ name: k, value: v.total }));
  const payData = Object.entries(summary.by_payment_method || {}).map(([k, v]) => ({ name: k, value: v.total }));

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Expenses</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Track company spending — category, payment method, vendor & references</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => downloadPdf('monthly')}
            disabled={downloading === 'monthly'}
            className="border-brand-border rounded-sm"
            data-testid="download-monthly-expenses-pdf-btn"
          >
            <FileText size={14} className="mr-2" />
            {downloading === 'monthly' ? 'Preparing...' : 'Monthly PDF'}
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadPdf('all')}
            disabled={downloading === 'all'}
            className="border-brand-border rounded-sm"
            data-testid="download-alltime-expenses-pdf-btn"
          >
            <Download size={14} className="mr-2" />
            {downloading === 'all' ? 'Preparing...' : 'All-Time PDF'}
          </Button>
          <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-expense-btn">
            <Plus size={16} className="mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="space-y-1">
          <Label className="text-xs">Period</Label>
          <Input type="month" value={periodFilter} onChange={e => setPeriodFilter(e.target.value)} className="bg-white border-brand-border rounded-sm w-44" data-testid="expense-period" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-brand-error/10 flex items-center justify-center"><TrendingDown size={24} className="text-brand-error" /></div>
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Total Expenses</p>
              <p className="font-heading text-2xl text-brand-error">{(summary.total || 0).toLocaleString()}</p>
              <p className="text-xs text-brand-text-secondary mt-1">{expenses.length} entries</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-brand-text-secondary mb-2">By Category</p>
            {chartData.length > 0 ? (
              <div className="flex items-center gap-3">
                <ResponsiveContainer width={110} height={110}>
                  <PieChart><Pie data={chartData} dataKey="value" cx="50%" cy="50%" outerRadius={48} innerRadius={28}>
                    {chartData.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} />)}
                  </Pie><Tooltip /></PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1">
                  {chartData.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="text-brand-text-secondary truncate">{d.name}</span></div>
                      <span className="font-medium text-brand-text-primary">{d.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-brand-text-secondary text-sm text-center py-4">No data</p>}
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-brand-text-secondary mb-2">By Payment Method</p>
            {payData.length > 0 ? (
              <div className="space-y-1.5">
                {payData.map((d, i) => {
                  const pct = summary.total > 0 ? (d.value / summary.total * 100) : 0;
                  return (
                    <div key={d.name} className="space-y-0.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-brand-text-secondary">{d.name}</span>
                        <span className="font-medium text-brand-text-primary">{d.value.toLocaleString()} <span className="text-brand-text-secondary">({pct.toFixed(1)}%)</span></span>
                      </div>
                      <div className="h-1.5 bg-brand-bg rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-brand-text-secondary text-sm text-center py-4">No data</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Date', 'Title', 'Description', 'Category', 'Payment', 'Vendor', 'Ref No.', 'Amount', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="py-8 text-center text-brand-text-secondary">Loading...</td></tr>
                ) : expenses.length === 0 ? (
                  <tr><td colSpan={9} className="py-8 text-center text-brand-text-secondary">No expenses</td></tr>
                ) : expenses.map(e => (
                  <tr key={e.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-3 text-brand-text-secondary text-xs whitespace-nowrap">{e.date}</td>
                    <td className="py-3 px-3 text-brand-text-primary font-medium">{e.title}</td>
                    <td className="py-3 px-3 text-brand-text-secondary text-xs max-w-[200px] truncate">{e.description || e.notes || '-'}</td>
                    <td className="py-3 px-3"><Badge className="text-xs bg-brand-bg text-brand-text-secondary border-0">{e.category}</Badge></td>
                    <td className="py-3 px-3"><Badge className="text-xs bg-brand-primary/10 text-brand-primary border-0">{e.payment_method || '-'}</Badge></td>
                    <td className="py-3 px-3 text-brand-text-secondary text-xs">{e.vendor || '-'}</td>
                    <td className="py-3 px-3 font-mono text-brand-text-secondary text-xs">{e.reference_no || '-'}</td>
                    <td className="py-3 px-3 font-medium text-brand-error whitespace-nowrap">{Number(e.amount).toLocaleString()}</td>
                    <td className="py-3 px-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(e)} className="h-7 w-7" data-testid={`edit-expense-${e.id}`}><Pencil size={12} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(e.id)} className="h-7 w-7 text-brand-error" data-testid={`delete-expense-${e.id}`}><Trash2 size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Expense' : 'New Expense'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Office rent — October" data-testid="expense-title" className="bg-white border-brand-border rounded-sm" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Full details about this expense" data-testid="expense-description" className="bg-white border-brand-border rounded-sm" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" className="bg-white border-brand-border rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="expense-date" className="bg-white border-brand-border rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="expense-payment-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor / Paid To</Label>
                <Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} placeholder="e.g., AWS, Zomato, SBI" data-testid="expense-vendor" className="bg-white border-brand-border rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Reference / Receipt No.</Label>
                <Input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} placeholder="e.g., INV-1023, UPI txn id" data-testid="expense-reference" className="bg-white border-brand-border rounded-sm" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Internal Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-white border-brand-border rounded-sm" rows={2} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="expense-save-btn">{editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
